import { socketEvent, timerActiveChangedMessageSchema } from "@orsane/schema/realtime";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { RotateCcwClock } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { RUNNING_PULSE_DURATION } from "@/components/timer/RunningClock";
import { useElapsedSeconds } from "@/components/timer/useElapsedSeconds";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { formatDurationWithSeconds } from "@/lib/format";
import { useSocketMessage } from "@/lib/socket/SocketContext";
import { useTRPC } from "@/lib/trpc";
import { useActiveTimer } from "@/queries/timer-queries";

/**
 * The app-wide running-timer indicator. Visible only while a timer is running for this
 * user in this workspace; hovering names the task, clicking opens it.
 *
 * The only subscriber for `timer:active` — see TimerRealtimeBridge for why that matters.
 */
export function SidebarRunningTimer() {
    const trpc = useTRPC();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const { data: active } = useActiveTimer();

    useSocketMessage({
        event: socketEvent.timer_active_changed,
        schema: timerActiveChangedMessageSchema,
        callback: () => {
            queryClient.invalidateQueries({ queryKey: trpc.timer.active.queryKey() });
        },
    });

    // The elapsed time ticks locally off startedAt, so the tooltip stays live without
    // any polling. Hooks must run before the early return.
    const elapsed = useElapsedSeconds(active?.accumulatedSeconds ?? 0, active?.startedAt ?? null);

    // The card should sit evenly inside the sidebar, which the tooltip is not anchored
    // to — it hangs off the button. Measured when the tooltip opens rather than
    // hardcoded, since the sidebar is resizable and the button does not sit at the
    // row's edge.
    const triggerRef = useRef<HTMLButtonElement>(null);
    const [card, setCard] = useState<{ width: number; alignOffset: number } | null>(null);
    const measureCard = useCallback((open: boolean) => {
        if (!open) return;
        const trigger = triggerRef.current;
        const footerRow = trigger?.parentElement;
        const sidebar = trigger?.closest('[data-slot="sidebar"]');
        if (!trigger || !footerRow || !sidebar) return;

        const rowBox = footerRow.getBoundingClientRect();
        const sidebarBox = sidebar.getBoundingClientRect();
        // The band the card is read against runs past the sidebar element: the resize
        // handle beside it is transparent, so the page background shows through and the
        // two read as one. Identified by its cursor rather than a class, and treated as
        // absent when the sidebar has no handle.
        const next = sidebar.parentElement?.nextElementSibling;
        const handle = next && getComputedStyle(next).cursor === "ew-resize" ? next : null;
        const bandRight = sidebarBox.right + (handle?.getBoundingClientRect().width ?? 0);

        // Mirror the footer row's left inset — the one the + button sits on — against
        // the far side of that band, so the gap looks the same either side.
        const inset = rowBox.left - sidebarBox.left;
        setCard({
            width: bandRight - sidebarBox.left - inset * 2,
            alignOffset: rowBox.left - trigger.getBoundingClientRect().left,
        });
    }, []);

    if (!active) return null;

    const openTask = () => navigate({ to: "/all", search: { task: active.taskId } });

    return (
        <Tooltip onOpenChange={measureCard}>
            <TooltipTrigger
                render={
                    <Button
                        ref={triggerRef}
                        variant="ghost"
                        size="sm"
                        // h-9 matches the size="icon" + button beside it in the footer.
                        className="h-9 gap-1.5 px-2 hover:bg-zinc-300/70"
                        aria-label={`Timer running on T-${active.taskNumber}`}
                        // The sidebar sits outside any task-bearing route, so `to: "."`
                        // (what useTaskNavigation does) has nothing to resolve against.
                        onClick={openTask}
                    >
                        {/* Still icon with a pulsing dot beside it: one element that has to
                            be noticed from anywhere, so the motion is confined to the dot. */}
                        <RotateCcwClock className="size-4 text-orsane-primary" />
                        <span
                            className="size-1.5 rounded-full bg-emerald-500 motion-safe:animate-pulse"
                            style={{ animationDuration: RUNNING_PULSE_DURATION }}
                        />
                    </Button>
                }
            />
            {/* Keeps the default dark tooltip surface; only the padding is dropped, so the
                divider can run the card's full width. Secondary text and the divider are
                therefore tinted with the background colour, not the muted token. */}
            <TooltipContent
                side="top"
                align="start"
                alignOffset={card?.alignOffset ?? 0}
                className="p-0"
                style={{ width: card?.width }}
            >
                {/* The whole card opens the task, not just the trigger underneath it.
                    rounded-md so the hover fill does not square off the bubble's corners. */}
                <button
                    type="button"
                    onClick={openTask}
                    className="flex w-full cursor-pointer flex-col rounded-md text-left hover:bg-background/10"
                >
                    <div className="flex flex-col gap-1 px-3 pt-2.5 pb-3">
                        <span className="flex items-center gap-1.5 text-background/80">
                            <span
                                className="size-1.5 rounded-full bg-emerald-400 motion-safe:animate-pulse"
                                style={{ animationDuration: RUNNING_PULSE_DURATION }}
                            />
                            Timer running
                        </span>
                        {/* The readout is the point of the card, so it gets the size.
                            Tabular figures keep it from twitching as the seconds turn. */}
                        <span className="font-medium text-2xl leading-none tracking-tight tabular-nums">
                            {formatDurationWithSeconds(elapsed)}
                        </span>
                    </div>
                    <div className="flex w-full flex-col gap-0.5 border-t border-background/15 px-3 py-2">
                        <span className="truncate font-medium text-sm">
                            <span className="mr-2">T-{active.taskNumber}</span> {active.taskTitle}
                        </span>
                    </div>
                </button>
            </TooltipContent>
        </Tooltip>
    );
}
